import { useMemo, useState, useCallback } from "react";
import { collection, query } from "firebase/firestore";
import { db } from "../lib/firebase";
import { useFirestoreCollectionOnce } from "./useFirestoreCollectionOnce";
import { searchHubSpotContacts } from "../services/hubspotService";
import type {
  SearchHubSpotContactsResponse,
  PersonInternal,
} from "@event-planner/shared";

/**
 * Hooks for the HubSpot integration (ADR-169 Stage 2, review-first per
 * ADR-172). Reads only — linking commits through the person form's
 * save paths (`personInternalService`), never from a hook here.
 */

export function useHubSpotSearch() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const search = useCallback(
    async (queryText: string): Promise<SearchHubSpotContactsResponse> => {
      setLoading(true);
      setError(null);
      try {
        return await searchHubSpotContacts({ query: queryText });
      } catch (err) {
        const e =
          err instanceof Error ? err : new Error("HubSpot search failed");
        setError(e);
        throw e;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return { search, loading, error };
}

interface UseAllHubSpotLinksResult {
  /** personId → hubspotContactId, for linked persons only. */
  links: Map<string, string>;
  loading: boolean;
  error: Error | null;
  refetch: () => void;
}

/**
 * One query over `personInternals` (organizer+ readable), reduced to a
 * personId → hubspotContactId map for the list pages' sprocket cells.
 * One-time fetch per the admin house pattern. Pass `enabled: false`
 * (e.g. when HubSpot is unconfigured) to skip the query entirely.
 */
export function useAllHubSpotLinks(enabled = true): UseAllHubSpotLinksResult {
  const q = useMemo(
    () => (enabled ? query(collection(db, "personInternals")) : null),
    [enabled],
  );
  const { data, loading, error, refetch } =
    useFirestoreCollectionOnce<PersonInternal>(q);

  const links = useMemo(() => {
    const map = new Map<string, string>();
    for (const internal of data) {
      if (internal.hubspotContactId) {
        map.set(internal.id, internal.hubspotContactId);
      }
    }
    return map;
  }, [data]);

  return { links, loading, error, refetch };
}
